import axios from 'axios'
import { bffBaseURL, normalizeError } from './apiClient'
import type { components } from '@/types/generated'

export class ApiResponseError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message)
    this.name = 'ApiResponseError'
  }
}

// ============================================================================
// Jobs API — Phase 57
// ============================================================================

export async function createJob(jobData: any) {
  try {
    const response = await axios.post(
      `${bffBaseURL}/api/v1/jobs`,
      jobData
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to create job')
      throw new Error(message)
    }
    throw error
  }
}

export async function getJobs(params: Record<string, any> = {}) {
  try {
    const queryString = new URLSearchParams(params).toString()
    const response = await axios.get(
      `${bffBaseURL}/api/v1/jobs${queryString ? '?' + queryString : ''}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to load jobs')
      throw new Error(message)
    }
    throw error
  }
}

export async function getJob(jobId: string) {
  try {
    const response = await axios.get(
      `${bffBaseURL}/api/v1/jobs/${jobId}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to get job')
      throw new Error(message)
    }
    throw error
  }
}

export async function updateJob(jobId: string, updates: any) {
  try {
    const response = await axios.patch(
      `${bffBaseURL}/api/v1/jobs/${jobId}`,
      updates
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to update job')
      throw new Error(message)
    }
    throw error
  }
}

export async function deleteJob(jobId: string) {
  try {
    await axios.delete(
      `${bffBaseURL}/api/v1/jobs/${jobId}`
    )
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to delete job')
      throw new Error(message)
    }
    throw error
  }
}

// ============================================================================
// Job Applications (Pipeline) API — Phase 57 M12
// ============================================================================

export async function addToPipeline(
  jobId: string,
  candidateId: string,
  status: string = 'applied',
  notes: string | null = null
): Promise<components['schemas']['JobApplicationResponse']> {
  try {
    const response = await axios.post(
      `${bffBaseURL}/api/v1/jobs/${jobId}/applications`,
      { candidate_id: candidateId, status, notes }
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to add to pipeline')
      throw new ApiResponseError(message, error.response?.status ?? 0)
    }
    throw error
  }
}

export async function getJobPipeline(jobId: string, params: Record<string, any> = {}) {
  try {
    const queryString = new URLSearchParams(params).toString()
    const response = await axios.get(
      `${bffBaseURL}/api/v1/jobs/${jobId}/applications${queryString ? '?' + queryString : ''}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to get pipeline')
      throw new Error(message)
    }
    throw error
  }
}

export async function getApplication(applicationId: string) {
  try {
    const response = await axios.get(
      `${bffBaseURL}/api/v1/jobs/applications/${applicationId}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to get application')
      throw new Error(message)
    }
    throw error
  }
}

export async function updateApplication(applicationId: string, updates: any) {
  try {
    const response = await axios.patch(
      `${bffBaseURL}/api/v1/jobs/applications/${applicationId}`,
      updates
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to update application')
      throw new Error(message)
    }
    throw error
  }
}

export async function removeFromPipeline(applicationId: string) {
  try {
    await axios.delete(
      `${bffBaseURL}/api/v1/jobs/applications/${applicationId}`
    )
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to remove from pipeline')
      throw new Error(message)
    }
    throw error
  }
}

export interface HireCandidatePayload {
  hire_date: string  // ISO date string YYYY-MM-DD
  /**
   * Explicit opening slot (F3). Omitted → upstream auto-assigns the earliest
   * active-unfilled opening, so legacy callers are unchanged (HireRequest is
   * additive).
   */
  opening_id?: string | null
  department?: string | null
  salary?: number | null
  salary_currency?: string | null
  notes?: string | null
}

export type HireResponse = components['schemas']['HireResponse']

export async function hireCandidate(jobId: string, appId: string, data: HireCandidatePayload): Promise<HireResponse> {
  try {
    const response = await axios.post(
      `${bffBaseURL}/api/v1/jobs/${jobId}/applications/${appId}/hire`,
      data
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to hire candidate')
      throw new Error(message)
    }
    throw error
  }
}

export type UnhireResponse = components['schemas']['UnhireResponse']

/**
 * Reverse a hire (F3 un-hire). `status` is REQUIRED and technical-only — the
 * reversal target (e.g. `applied`); it is never fabricated here, and a hire
 * cannot be reversed into `hired` (upstream guard). `X-User-ID` attribution is
 * forwarded by the BFF from the session.
 */
export async function unhireApplication(
  jobId: string,
  applicationId: string,
  status: string
): Promise<UnhireResponse> {
  try {
    const payload: components['schemas']['UnhireRequest'] = { status }
    const response = await axios.post(
      `${bffBaseURL}/api/v1/jobs/${jobId}/applications/${applicationId}/un-hire`,
      payload
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError(
        (error.response?.data as any)?.detail || 'No se pudo revertir la contratación'
      )
      throw new ApiResponseError(message, error.response?.status ?? 0)
    }
    throw error
  }
}

export async function getCandidateApplications(candidateId: string, params: Record<string, any> = {}) {
  try {
    const queryString = new URLSearchParams(params).toString()
    const response = await axios.get(
      `${bffBaseURL}/api/v1/candidates/${candidateId}/applications${queryString ? '?' + queryString : ''}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to get candidate applications')
      throw new Error(message)
    }
    throw error
  }
}

// ============================================================================
// Matching API — Phase 57
// ============================================================================

export async function matchCandidates(matchRequest: any) {
  try {
    const response = await axios.post(
      `${bffBaseURL}/api/v1/match`,
      matchRequest
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Matching failed')
      throw new Error(message)
    }
    throw error
  }
}

export async function getJobCandidates(jobId: string, params: Record<string, any> = {}) {
  try {
    const queryString = new URLSearchParams(params).toString()
    const response = await axios.get(
      `${bffBaseURL}/api/v1/match/${jobId}/candidates${queryString ? '?' + queryString : ''}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError((error.response?.data as any)?.detail || 'Failed to get job candidates')
      throw new Error(message)
    }
    throw error
  }
}

// ============================================================================
// Job Posting Notes — Phase 121 F2 (Stage 6.1)
//
// Typed end to end from the generated contract (X2/DL-16): no hand-written DTOs
// and no `any` on the response, because the wrong-level/renamed-field read is the
// exact bug class B3 came from.
// ============================================================================

export type JobNote = components['schemas']['JobNoteResponse']
export type JobNotesList = components['schemas']['JobNotesListResponse']

export async function getJobNotes(
  jobId: string,
  params: { limit?: number } = {}
): Promise<JobNotesList> {
  try {
    const queryString = new URLSearchParams(
      Object.entries(params).reduce<Record<string, string>>((acc, [k, v]) => {
        if (v !== undefined) acc[k] = String(v)
        return acc
      }, {})
    ).toString()
    const response = await axios.get(
      `${bffBaseURL}/api/v1/jobs/${jobId}/notes${queryString ? '?' + queryString : ''}`
    )
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError(
        (error.response?.data as any)?.detail || 'No se pudieron cargar las notas'
      )
      throw new ApiResponseError(message, error.response?.status ?? 0)
    }
    throw error
  }
}

/**
 * Create a note.
 *
 * `noteId` is the D17 idempotency key: the composer mints ONE id when the user
 * starts writing and passes the SAME id on every retry, so a note that committed
 * before its response was lost is not written twice. A fresh id per click puts the
 * duplicate bug straight back.
 */
export async function addJobNote(
  jobId: string,
  content: string,
  noteId?: string
): Promise<JobNote> {
  try {
    const payload: components['schemas']['JobNoteCreateRequest'] = { content }
    if (noteId) payload.id = noteId
    const response = await axios.post(`${bffBaseURL}/api/v1/jobs/${jobId}/notes`, payload)
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError(
        (error.response?.data as any)?.detail || 'No se pudo guardar la nota'
      )
      throw new ApiResponseError(message, error.response?.status ?? 0)
    }
    throw error
  }
}

// ============================================================================
// Job Openings — Phase 121 F3 (Stage 11.1)
//
// Filled-ness is DERIVED server-side (`is_filled` = a hired application points
// at the opening). The client renders it, never computes or stores it: there is
// no `filled` status token — `status` is ONLY `active` | `cancelled`
// (design.md:151).
// ============================================================================

// Generated contract aliases (task 10.2): the hand-written DTOs these replace
// were a stopgap so 11.1 could land before generated.ts was regenerated. They
// are now plain aliases into `components['schemas']` — one source of truth, no
// shape drift (X2/DL-16). The alias NAMES stay exported because the Aperturas
// tab and pages already import them.
export type JobOpeningHiredApplication = components['schemas']['OpeningHireResponse']
export type JobOpening = components['schemas']['OpeningResponse']
export type JobOpeningsSummary = components['schemas']['OpeningsListResponse']

/** Grow: add one opening to the position. Dates are optional (inherit position dates). */
export async function addJobOpening(
  jobId: string,
  dates: { start_date?: string | null; end_date?: string | null } = {}
): Promise<JobOpening> {
  try {
    const payload: { start_date?: string | null; end_date?: string | null } = {}
    if (dates.start_date !== undefined) payload.start_date = dates.start_date
    if (dates.end_date !== undefined) payload.end_date = dates.end_date
    const response = await axios.post(`${bffBaseURL}/api/v1/jobs/${jobId}/openings`, payload)
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError(
        (error.response?.data as any)?.detail || 'No se pudo agregar la apertura'
      )
      throw new ApiResponseError(message, error.response?.status ?? 0)
    }
    throw error
  }
}

/**
 * Cancel or edit one opening (targeted action). `status` is technical-only —
 * `active` | `cancelled`; filled-ness is never written (guards are upstream).
 * 409 (filled-opening cancel / shrink floor) and 422 arrive as
 * `ApiResponseError` so the Aperturas tab can surface them inline.
 */
export async function updateOpening(
  openingId: string,
  updates: {
    status?: 'active' | 'cancelled'
    start_date?: string | null
    end_date?: string | null
  }
): Promise<JobOpening> {
  try {
    const response = await axios.patch(`${bffBaseURL}/api/v1/openings/${openingId}`, updates)
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError(
        (error.response?.data as any)?.detail || 'No se pudo actualizar la apertura'
      )
      throw new ApiResponseError(message, error.response?.status ?? 0)
    }
    throw error
  }
}

/**
 * Read a position's openings with server-derived counts. `active_count` IS the
 * derived `vacancies` — the tab must render it, never recompute it (X6: no
 * client-side derivation that could disagree with the job payload).
 */
export async function getJobOpenings(jobId: string): Promise<JobOpeningsSummary> {
  try {
    const response = await axios.get(`${bffBaseURL}/api/v1/jobs/${jobId}/openings`)
    return response.data
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = normalizeError(
        (error.response?.data as any)?.detail || 'No se pudieron cargar las aperturas'
      )
      throw new ApiResponseError(message, error.response?.status ?? 0)
    }
    throw error
  }
}
